/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
/* eslint-disable max-lines-per-function */
import { type OnDestroy, type OnInit, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { type ActionCloseArgs, EventService, HttpService, I18nService, LazyloadService, PermissionService, PromptService,
TooltipDirective } from '@dm/core';
import { AuthService, SmartGuiService, ToolService } from 'app/common/services';
import { ProductConfig } from 'app/config';
import { FeatureConfig } from 'app/config/feature';
import { EulaComponent } from 'app/feature/settings/eula/eula.component';
import { EulaService } from 'app/feature/settings/eula/eula.service';
import { EulaStrService } from 'app/feature/settings/eula/eula-str.service';
import { type Subscription, forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { BatchConfigComponent } from './batch-config/batch-config.component';
import { ExportComponent } from './export/export.component';
import { type HeaderSettingsItem } from './header-settings';
import { RestartShutdownComponent } from './restart-shutdown/restart-shutdown.component';
import { SystemInfoComponent } from './system-info/system-info.component';
import { OtaUpgradeComponent } from './upgrade/ota-upgrade-panel/ota-upgrade.component';
import { SystemUpgradeComponent } from './upgrade/system-upgrade-panel/system-upgrade.component';
import { CmoQueryFrom, CmoQueryTypeE, CmoUpdSysModeE, CmoUpdSysStepTaskStatusE } from './upgrade/upgrade.const';
import { UpgradeService } from './upgrade/upgrade.service';
import { UpgradeChooseModeComponent } from './upgrade/upgrade-choose-mode/upgrade-choose-mode.component';
import { UpgradeComponent } from './upgrade/upgrade-panel/upgrade.component';
import { UpgradeStrService } from './upgrade/upgrade-str.service';
import { UpgradeUploadEntryComponent } from './upgrade/upload-entry/upgrade-upload-entry.component';

@Component({
    selector: 'app-header-settings',
    templateUrl: './header-settings.component.html',
    styleUrls: ['./header-settings.component.scss'],
})

export class HeaderSettingsComponent implements OnInit, OnDestroy {
    @Input() isSmallSize = false;
    @Input() upgrade: UpgradeComponent;
    @Input() systemUpgrade: SystemUpgradeComponent;
    @Input() otaUpgrade: OtaUpgradeComponent;
    @Input() enableUpgradeEntry = false;

    @Output() destroy: EventEmitter<any> = new EventEmitter();
    @Output() onHidePanel = new EventEmitter();
    @ViewChild('tooltip') tooltip: TooltipDirective;

    items = [];
    tooltipVisible = false;

    /**是否白牌 */
    isOem = this.toolService.isOem();

    private sub$: Subscription;
    private upgrade$: Subscription;

    constructor(
        private lazyloadService: LazyloadService,
        private i18n: I18nService,
        private permissionService: PermissionService,
        private router: Router,
        public smartguiService: SmartGuiService,
        private eventService: EventService,
        private http: HttpService,
        private promptService: PromptService,
        private upgradeStr: UpgradeStrService,
        private toolService: ToolService,
        private upgradeService: UpgradeService,
        private eulaService: EulaService,
        private eulaStrService: EulaStrService,
        private authService: AuthService,
    ) { }

    ngOnInit(): void {
        this.getEulaInfo();
    }

    /**
     * 查询是否签署EULA协议，白牌不支持
     */
    getEulaInfo(): void {
        this.eulaService.checkEulaInfo()
            .pipe(finalize(() => {
                this.setItems();
            }))
            .subscribe();
    }

    getUpgradeItem(): Array<HeaderSettingsItem> {
        return ProductConfig.isHideUpgrade ? [] : [{
            label: this.i18n.get('common.upgrade_label'),
            // 默认打开ota升级
            command: (isAutoUpgrade: boolean): void => {
                this.isShowUpgradeUploadEntry(isAutoUpgrade);
            },
            permission: ['support_major_W'],
            supperAdmin: true,
        }];
    }

    setItems(): void {
        this.items = [
            {
                label: this.i18n.get('common.device_info_label'),
                command: (): void => {
                    this.lazyloadService.appendComponent(SystemInfoComponent);
                    this.tooltipVisible = false;
                    this.onHidePanel.emit();
                },
            },
            {
                label: this.i18n.get('common.device_restart_label'),
                divide: true,
                command: (): void => {
                    this.lazyloadService.appendComponent(RestartShutdownComponent, { action: 'Restart' });
                    this.tooltipVisible = false;
                    this.onHidePanel.emit();
                },
                permission: ['boot_W'],
            },
            {
                label: this.i18n.get('common.device_shutdown_label'),
                command: (): void => {
                    this.lazyloadService.appendComponent(RestartShutdownComponent, { action: 'Shutdown' });
                    this.tooltipVisible = false;
                    this.onHidePanel.emit();
                },
                permission: ['boot_W'],
            },
            ...this.getUpgradeItem(),
            {
                label: this.i18n.get('common.batchConfig_label'),
                divide: true,
                permission: ['@batch_configuration_R'],
                command: (): void => {
                    this.lazyloadService.appendComponent(BatchConfigComponent);
                    this.tooltipVisible = false;
                    this.onHidePanel.emit();
                },
            },
            {
                label: this.i18n.get('system.exportData_task_title_label'),
                command: (): void => {
                    this.lazyloadService.appendComponent(ExportComponent);
                    this.tooltipVisible = false;
                    this.onHidePanel.emit();
                },
                permission: [['alarm_W', 'running_data_W', 'performance_W']],
            },
            {
                label: this.eulaStrService.i18n.eulaLabel,
                command: (): void => {
                    this.lazyloadService.appendComponent(EulaComponent);
                    this.tooltipVisible = false;
                    this.onHidePanel.emit();
                },
            },
        ].filter((item) => {
            if (item.label === this.eulaStrService.i18n.eulaLabel) {
                return this.eulaService.getIsSignEulaNeeded() || this.eulaService.getIsSignedEula();
            }

            const show = this.permissionService.checkShow(item.permission);
            const isSuper = item.supperAdmin ? this.permissionService.isSuperAdmin() : true;

            if (!FeatureConfig.IsShowDeviceRestartAndShutDownButton && (item.label === this.i18n.get('common.device_restart_label') ||
                item.label === this.i18n.get('common.device_shutdown_label'))) {
                return false;
            }

            //远程协助管理员不支持导出日志
            if (this.authService.isRemoteAssistanceUser() && item.label === this.i18n.get('system.exportData_task_title_label')) {
                return false;
            }

            return show && isSuper;
        });

        if (!this.items.length) {
            this.destroy.emit();
        }

        if (this.items.length && this.items[0].divide) {
            this.items[0].divide = false;
        }

        this.sub$ = this.router.events.subscribe((evt) => {
            if (evt instanceof NavigationEnd && this.tooltipVisible) {
                this.tooltipVisible = false;
                this.onHidePanel.emit();
            }
        });

        if (this.enableUpgradeEntry) {
            this.upgrade$ = this.eventService.subscribe('upgrade$', (isAutoUpgrade: boolean) => {
                const _label = this.i18n.get('common.upgrade_label');
                const upgradeBtn = this.items.find((item) => item.label === _label);
                if (upgradeBtn) {
                    upgradeBtn.command(isAutoUpgrade);
                }
            });
        }
    }

    /**
     * 打开升级入口 1.当没有升级标志2.查询升级状态接口通过,可以打开
     *
     * @param isOtaUpgrade  true ota升级(默认)，false 手动升级
     */
    isShowUpgradeUploadEntry(isOtaUpgrade = true): void {
        const confObj = {
            title: this.upgradeStr.i18n.eventLevelInfo,
            content: this.upgradeStr.i18n.upgradingInfo,
            onOk: (): void => {
                this.promptService.close();
            },
        };

        const isVisible = (): void => {
            this.tooltipVisible = false;
            this.onHidePanel.emit();
        };
        // 页面有升级图标
        if (this.upgrade.iconVisible || this.systemUpgrade.iconVisible || this.otaUpgrade.iconVisible) {
            isVisible();
            this.promptService.prompt(confObj);
            return;
        }
        const upgradeStatus$ = this.http.get(this.http.makeUrl('upgrade.upgradeProgress')
            .setParams({ CMO_QUERY_TYPE: CmoQueryTypeE.Hotpatch, CMO_QUERY_FROM: CmoQueryFrom.DM }),
                { mask: true, doException: false });
        // 系统升级包含了快速升级和OTA升级
        const systemUpgrade$ = this.http.get(this.http.makeUrl('upgrade.upgradeProgress')
            .setParams({ CMO_QUERY_TYPE: CmoQueryTypeE.SystemUpgrade, CMO_QUERY_FROM: CmoQueryFrom.DM }),
                { mask: true, doException: false });

        forkJoin(upgradeStatus$, systemUpgrade$).subscribe(([hostPatchStatus, systemUpgrade]) => {
            // 处于快速升级/ota升级流程中。正在升级 正在回滚 升级暂停 回滚失败 回滚暂停
            const inFastOrOta = [
                    CmoUpdSysModeE.FastUpgrade,
                    CmoUpdSysModeE.OtaUpgrade,
                ].includes(Number(systemUpgrade.data.CMO_UPD_SYS_MODE)) && [
                    CmoUpdSysStepTaskStatusE.Upgrading,
                    CmoUpdSysStepTaskStatusE.Rollbacking,
                    CmoUpdSysStepTaskStatusE.UpgradePause,
                    CmoUpdSysStepTaskStatusE.RollbackFail,
                    CmoUpdSysStepTaskStatusE.RollbackPause,
                ].includes(Number(systemUpgrade.data.CMO_UPD_SYS_STEP_TASK_STATUS));

            // 处于热补丁升级流程中。正在升级
            const inHotpath = Number(hostPatchStatus.data.CMO_UPD_SYS_MODE) === 5 &&
                Number(hostPatchStatus.data.CMO_UPD_SYS_STEP_STEP_PERCENT) !== 100;

            const isUpgrade = inFastOrOta || inHotpath;

            // 正在升级
            if (isUpgrade) {
                isVisible();
                this.promptService.prompt(confObj);
                return;
            }

            const supportOtaUpgrade = isOtaUpgrade && !this.toolService.isOem() && this.upgradeService.ifShowUpgrade() && !ProductConfig.isHideUpgrade;

            // oem、高端release环境不支持ota升级
            if (supportOtaUpgrade) {
                // 跳转到ota升级
                this.router.navigate(['/settings/basic-infomation/upgrade']);
            } else {
                // 打开手动升级方式选择侧滑框
            this.lazyloadService.appendComponent(UpgradeChooseModeComponent, {
                upgrade: this.upgrade,
                systemUpgrade: this.systemUpgrade,
                onClose: ({ upgradeMode }: ActionCloseArgs) => {
                    if (!upgradeMode) {
                        return;
                    }
                    this.lazyloadService.appendComponent(UpgradeUploadEntryComponent, {
                        upgrade: this.upgrade,
                        systemUpgrade: this.systemUpgrade,
                        upgradeMode,
                    });
                },
            });
            }
            this.tooltipVisible = false;
            this.onHidePanel.emit();
        });
    }

    ngOnDestroy(): void {
        this.sub$.unsubscribe();
        if (this.upgrade$) {
            this.upgrade$.unsubscribe();
        }
    }
}
